import React, { useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CheckCircle, UploadFile } from '@mui/icons-material';

interface FileUploadStepProps {
  onFileSelected: (file: File) => void;
  loading: boolean;
  selectedFile: File | null;
  error?: string | null;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  onFileSelected,
  loading,
  selectedFile,
  error,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelected(file);
  };

  return (
    <Paper sx={{ p: 3 }} data-testid="file-upload-step">
      <Typography variant="h6" gutterBottom data-testid="file-upload-header">
        Upload CSV File
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a CSV file containing your site data. The file will be validated
        and previewed before import.
      </Typography>

      {/* File Selection Button */}
      <Box sx={{ mb: 3 }}>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          ref={fileInputRef}
          style={{ display: 'none' }}
          data-testid="file-upload-input"
        />

        <Button
          variant="contained"
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <UploadFile />
            )
          }
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          size="large"
          data-testid="file-upload-button"
        >
          {loading ? 'Processing...' : 'Select CSV File'}
        </Button>

        {selectedFile && !loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1 }}
            data-testid="file-selected-info"
          >
            Selected: {selectedFile.name}
          </Typography>
        )}
      </Box>

      {/* File Format Information */}
      <Box sx={{ mb: 3 }} data-testid="file-upload-requirements">
        <Typography variant="subtitle2" gutterBottom>
          CSV File Requirements:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Must be a CSV file (.csv extension)"
              secondary="Contains plot and labour item data"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Proper column headers"
              secondary="File will be validated during upload"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Missing labour items will be identified"
              secondary="You can create them during the preview step"
            />
          </ListItem>
        </List>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="file-upload-error">
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default FileUploadStep;
